import { AxiosError } from 'axios';

export const ASSIGNMENT_CONCURRENCY = 4;
const API_REQUEST_CONCURRENCY = 6;
const RETRY_ATTEMPTS = 2;
const RETRY_BASE_DELAY_MS = 500;
const MAX_RETRY_AFTER_MS = 10000;
const RETRYABLE_STATUSES = [408, 429];

export class CancelledError extends Error {
	constructor() {
		super('Cancelled');
		this.name = 'CancelledError';
	}
}

export interface Limiter {
	<T>(task: () => Promise<T>): Promise<T>;
	clear: () => void;
}

interface QueuedTask {
	start: () => void;
	cancel: () => void;
}

export const createLimiter = (concurrency: number): Limiter => {
	let active = 0;
	let queue: QueuedTask[] = [];

	const next = () => {
		if (active >= concurrency) return;
		const task = queue.shift();
		if (!task) return;
		active += 1;
		task.start();
	};

	const limit = (<T>(task: () => Promise<T>) =>
		new Promise<T>((resolve, reject) => {
			queue.push({
				start: () => {
					Promise.resolve()
						.then(task)
						.then(resolve, reject)
						.finally(() => {
							active -= 1;
							next();
						});
				},
				cancel: () => reject(new CancelledError())
			});
			next();
		})) as Limiter;

	limit.clear = () => {
		const pending = queue;
		queue = [];
		pending.forEach((task) => task.cancel());
	};

	return limit;
};

export const isRetryableError = (error: unknown): boolean => {
	const axiosError = error as AxiosError | undefined;
	if (!axiosError?.isAxiosError) return false;
	const status = axiosError.response?.status;
	if (status === undefined) return true;
	return RETRYABLE_STATUSES.includes(status) || status >= 500;
};

const retryAfterMs = (error: unknown): number | undefined => {
	const header = (error as AxiosError).response?.headers?.['retry-after'];
	const seconds = Number(header);
	if (!header || !Number.isFinite(seconds)) return undefined;
	return Math.min(seconds * 1000, MAX_RETRY_AFTER_MS);
};

const wait = (ms: number) =>
	new Promise<void>((resolve) => setTimeout(resolve, ms));

export const withRetry = async <T>(
	task: () => Promise<T>,
	attempts = RETRY_ATTEMPTS,
	baseDelayMs = RETRY_BASE_DELAY_MS
): Promise<T> => {
	for (let attempt = 0; ; attempt += 1) {
		try {
			return await task();
		} catch (error) {
			if (attempt >= attempts || !isRetryableError(error)) throw error;
			await wait(
				retryAfterMs(error) ??
					baseDelayMs * 2 ** attempt +
						Math.random() * (baseDelayMs / 2)
			);
		}
	}
};

const apiRequestLimiter = createLimiter(API_REQUEST_CONCURRENCY);

export const throttledRequest = <T>(task: () => Promise<T>): Promise<T> =>
	apiRequestLimiter(() => withRetry(task));
